import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import classNames from 'classnames';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil } from 'ml-portals-util-js';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLTagManagerService } from 'ml-portals-services-react'
import appConfig from 'app-config';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { WatercraftOperatorPopupComponent } from 'ml-capability-quoteandbind-common-react';

import metadata from './WatercraftOperatorPagePolChange.metadata.json5';
import messages from './WatercraftOperatorPagePolChange.messages';
import styles from './WatercraftOperatorPagePolChange.module.scss';

const alertNames = {
    addedWatercraftOperatorName: 'addedWatercraftOperator'
};

const WatercraftOperatorPagePolChange = (props) => {
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        currentStep,
        changeNextSteps
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    
    const {
        showProgressDialog
    } = MLModalService();

    const isGPA = appConfig.portalCode === 'GPA';
    const pupModel = _.get(submissionVM, 'lobData.personalUmbrella', []);
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');

    const [watercraftOperator, setWatercraftOperator] = useState(viewModelService.create({}, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftOperatorDTOML'));
    const [isInSync, setInSync] = useState(false);
    const [pageAction, setPageAction] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const { onValidate } = useValidation('WatercraftOperatorPagePolChange');

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const setCurrentWatercraftOperatorVM = (lobVM) => {
        const currentWatercraftOperatorVMs = _.get(lobVM, 'personalUmbrella.coverables.watercraftOperators');
       
        const { additionalData } = currentStep;
        let watercraftOperatorVM = viewModelService.create({}, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftOperatorDTOML')
        const selectedWatercraftOperatorVM = currentWatercraftOperatorVMs.children.find((wc) => wc.value.isSelected === true);
        
        if(additionalData){
            // if Additional Data is availabe
            watercraftOperatorVM = currentWatercraftOperatorVMs.children.find((wc) => wc.value.fixedID === additionalData.fixedID);
        }
        
        if(!_.isUndefined(selectedWatercraftOperatorVM)){
            // if the isSelected is available
            watercraftOperatorVM = selectedWatercraftOperatorVM
        }

        return watercraftOperatorVM;
    };

    const initWatercraftOperatorPage = () => {
        setWatercraftOperator(setCurrentWatercraftOperatorVM(submissionVM.lobData));
        
        let pageActionLabel = MLPolicyChangeDataHolderService.getPageAction()
        
        if (currentStep.pageAction) {
            pageActionLabel = currentStep.pageAction
        }
        
        if (pageActionLabel === 'add') {
            setPageAction('add');
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addWatercraftOperatorDynamic), false, currentStep.mlStepName, authHeader);

        } else if (pageActionLabel === 'edit') {
            setPageAction('edit');
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editWatercraftOperatorDynamic), false, currentStep.mlStepName, authHeader);
        }

    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initWatercraftOperatorPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const getFullName = (person) => {
        const nameSuffixList = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
        const middleName = (person.middleName) ? person.middleName : '';
        const suffixTypeKeyName = (person.suffix) ? nameSuffixList.getCode(person.suffix).name : '';
        const suffixDisplayName = translator(
            {
                id: suffixTypeKeyName,
                defaultMessage: ' '
            }
        );
        return `${person.firstName} ${middleName} ${person.lastName} ${suffixDisplayName}`;
    };


    const addNewWatercraftOperatorAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.addedWatercraftOperatorName,
            formattedMessageName: 'watercraftoperatorXHasBeenAdded',
            messagePram: getFullName(item.contact.value)
        };
        const alertExists = alertMessages.some((msg) => {
            if (_.isEqual(msg, alertMsg)) {
                return true;
            }
            
            return false;
        });

        if (!alertExists) {
            alertMessages.push(alertMsg);
        }
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode); 
    };

    const saveEndorsement = () => {
        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            const WatercraftOperators = _.get(updatedEndorsementVM, 'value.lobData.personalUmbrella.coverables.watercraftOperators');
            const WatercraftOperatorsBeforeUpdate = _.get(submissionVM, 'value.lobData.personalUmbrella.coverables.watercraftOperators');
            const currentOperator = (a, b) => b.filter(x => !x.publicID || !a.some(y => y.publicID === x.publicID));

            let selectedWatercraftOperator = currentOperator(WatercraftOperatorsBeforeUpdate,WatercraftOperators)[0];

            if(_.isUndefined(selectedWatercraftOperator)){
                selectedWatercraftOperator = {...WatercraftOperators[0]}
            }

            const details={
                data: getFullName(watercraftOperator.contact.value),
                fixedId:selectedWatercraftOperator.fixedID,
                additionalData : selectedWatercraftOperator,
                pageAction 
            }

            updateWizardSubmissionData(updatedEndorsementVM);
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader ,currentStep);
            const action = watercraftOperator.value.isEdit ? 'Edit' : 'Add';
            MLTagManagerService.sendFormStepPolicyChangeStep(`${action} Watercraft Operator`);
            jumpToSelectedStepByStepID('endorsementAdditionalChanges');        
        }).catch((error) => {
            console.error(error);
        });
    };


    const onNext = () => {
        watercraftOperator.contact.updated.value = true;
        const isValid = watercraftOperator.aspects.valid && watercraftOperator.aspects.subtreeValid;
        if (!isValid) {
            setShowValidationErrors(true);
        }
        if (!isValid) return;

        if (pageAction === 'add') {
            addNewWatercraftOperatorAlert(watercraftOperator);
        }
        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);
        setInSync(true);
        saveEndorsement();
    };

    const resolvers = {
        resolveComponentMap: {
            watercraftOperatorPopupComponent: WatercraftOperatorPopupComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        addEditHeaderTitle: {
            className: classNames('page-header', !isGPA ? 'vertical-spacing-60' : ''),
            visible: pageAction === 'add' || pageAction === 'edit',
            content: pageAction === 'add' ? translator(messages.addWatercraftOperator) : translator(messages.editWatercraftOperator)
        },
        watercraftOperatorAddEditContainer: {
            className: !isGPA ? 'vertical-spacing-40' : ''
        },
        watercraftOperatorAddEditComponent: {
            pupModel,
            onValidate,
            showValidationErrors,
            submissionVM,
            watercraftOperatorVM: watercraftOperator,
            showProgressDialog,
            showActionRow: false,
            onModelChange: () => refreshData(),
            isEdit: pageAction === 'edit'
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.makeChanges : null}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
        </WizardPage>
    );

};

WatercraftOperatorPagePolChange.propTypes = wizardProps;
export default WatercraftOperatorPagePolChange;